import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  SimpleGrid,
  Spacer,
  Stack,
  Stat,
  StatLabel,
  StatNumber,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  TableContainer,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import {
  useFetchAccountsQuery,
  useFetchPurchaseOrderDetailQuery,
  useFetchPurchaseOrderTransactionsQuery,
  useFetchSomeAfesQuery,
  useFetchSomeUsersQuery,
} from 'api';
import { useAuth } from 'core';
import {
  AfeLink,
  ApproversSection,
  CommitmentVsBudgetBar,
  CurrencyLabel,
  DateDisplay,
  DateStack,
  InvoicedVsCommitmentBar,
  LoadingSkeleton,
  MultiBar,
  PageBody,
  PageCard,
  PageHeader,
  UserLink,
} from 'features';
import { useMemo, useState } from 'react';
import { FiEdit, FiPlus } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import { EditPoLineItemsModal } from './EditPoLineItemsModal';
import {
  PurchaseOrderFormWorkflowDrawer,
  PurchaseOrderFormWorkflowDrawerProps,
  PurchaseOrderFormWorkflowStage,
} from './PurchaseOrderFormWorkflowDrawer';
import {
  DecisionDrawer,
  DecisionDrawerProps,
} from 'features/components/DecisionDrawer';
import Decimal from 'decimal.js';

export interface PurchaseOrderDetailPageProps {}

export const PurchaseOrderDetailPage = (
  props: PurchaseOrderDetailPageProps
) => {
  const { id: sId } = useParams();
  const id = sId ?? '0';

  // Fetches
  const auth = useAuth();
  const { data: accountsResponse, refetch } = useFetchAccountsQuery(
    { clientId: auth?.user.clientId ?? '0' },
    { skip: !auth?.user.clientId }
  );

  const { data: po } = useFetchPurchaseOrderDetailQuery(id, {
    skip: !id,
  });
  // const [postPurchaseOrderTransaction] =
  //   usePostPurchaseOrderTransactionMutation();

  const afe = useFetchSomeAfesQuery(
    { ids: [po?.afeId || '0'] },
    { skip: !po?.afeId }
  );
  const user = useFetchSomeUsersQuery(
    { ids: [po?.assigneeUserId || '0'] },
    { skip: !po?.assigneeUserId }
  );
  const { data: transactions } = useFetchPurchaseOrderTransactionsQuery(
    { entityId: id, all: true },
    { skip: !id }
  );

  // State
  const [decisionDrawer, setDecisionDrawer] =
    useState<Omit<DecisionDrawerProps, 'onClose'>>();
  const [drawerSettings, setDrawerSettings] =
    useState<Omit<PurchaseOrderFormWorkflowDrawerProps, 'onClose'>>();
  const allSubAccounts = useMemo(() => {
    return accountsResponse?.items.flatMap((x) => x.subAccounts);
  }, [accountsResponse?.items]);

  const pendingRevision = useMemo(() => {
    return transactions?.items.find((x) => !x.redeemed && !x.cancelled);
  }, [transactions?.items]);

  // GH-41
  const isNewRevisionPossible = useMemo(() => {
    const liSums =
      po?.lineItems.reduce((sum, n) => {
        let saId = n.subAccountId;
        let subTotal = new Decimal(n.quantityOrdered ?? 0).mul(
          new Decimal(n.unitPrice ?? 0)
        );
        sum[saId] = new Decimal(sum[saId] ?? 0).add(subTotal).toString();
        return sum;
      }, {} as { [key: string]: string }) ?? {};
    const acctSums =
      po?.accounts.reduce((sum, n) => {
        let saId = n.subAccountId;
        let value = new Decimal((n.pending || n.total) ?? 0);
        sum[saId] = new Decimal(sum[saId] ?? 0).add(value).toString();
        return sum;
      }, {} as { [key: string]: string }) ?? {};
    const diffs =
      Object.keys(liSums)
        .map((k) => [k, liSums[k]])
        .reduce((sum, n) => {
          const [saId, liValue] = n;
          const acctValue = acctSums[saId];
          sum[saId] = new Decimal(sum[saId] ?? 0)
            .add(new Decimal(liValue).sub(new Decimal(acctValue)))
            .toString();
          return sum;
        }, {} as { [key: string]: string }) ?? {};
    console.debug(`po diffs`, diffs);
    return Object.keys(diffs)
      .map((k) => [k, diffs[k]])
      .some((pair) => {
        const [_, value] = pair;
        return !new Decimal(value).equals(0);
      });
  }, [po?.accounts, po?.lineItems]);
  // Methods

  return (
    <Box>
      {decisionDrawer && (
        <DecisionDrawer
          {...decisionDrawer}
          // onDecision={refetch}
          onClose={() => setDecisionDrawer(undefined)}
        />
      )}
      {po && drawerSettings && (
        <PurchaseOrderFormWorkflowDrawer
          {...drawerSettings}
          onClose={() => setDrawerSettings(undefined)}
          po={po}
        />
      )}
      <LoadingSkeleton isLoaded={!!po}>
        <Stack spacing="8">
          <PageHeader
            title={`Purchase Order: ${po?.name}`}
            subTitle={po?.description}
            topRightSection={
              po && (
                <Button
                  colorScheme="blue"
                  variant="solid"
                  leftIcon={
                    // @ts-ignore
                    <FiEdit />
                  }
                  onClick={() =>
                    setDrawerSettings({ ...drawerSettings, isOpen: true, po })
                  }
                >
                  Edit Purchase Order
                </Button>
              )
            }
          >
            <Stack spacing="4">
              <SimpleGrid columns={2}>
                <HStack>
                  <Text as="b">AFE:</Text>
                  <AfeLink afe={afe.data?.items.at(0)} />
                </HStack>
                <HStack>
                  <Text as="b">Asignee:</Text>
                  <UserLink u={user.data?.items?.at(0)} />
                </HStack>
                <HStack>
                  <Text as="b">Reference:</Text>
                  <Text>{po?.reference}</Text>
                </HStack>
                <HStack></HStack>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 2 }}>
                <Text as="b">Committed</Text>
                <MultiBar
                  max={[po?.totalBudgeted, po?.totalContingency]}
                  values={[po?.total]}
                />
                <Text as="b">Invoiced</Text>
                <MultiBar
                  max={[po?.total]}
                  values={[po?.totalInvoice, po?.pendingInvoice]}
                />
              </SimpleGrid>

              {/* <StatGroup>
                <Stat flex="1" margin="0 32px 0 0">
                  <StatLabel>Total</StatLabel>
                  <StatNumber>
                    <Currency value={po?.orderedTotal} />
                  </StatNumber>
                </Stat>
                <Stat flex="1" margin="0 32px 0 0">
                  <StatLabel>Pending</StatLabel>
                  <StatNumber>
                    <Currency value={po?.pendingTotal} />
                  </StatNumber>
                </Stat>
                <Stat flex="1" margin="0 32px 0 0">
                  <StatLabel>Received</StatLabel>
                  <StatNumber>
                    <Currency value={po?.receivedTotal} />
                  </StatNumber>
                </Stat>
                <Stat flex="1" margin="0 32px 0 0">
                  <StatLabel>Back Ordered</StatLabel>
                  <StatNumber>
                    <Currency value={po?.backOrderedTotal} />
                  </StatNumber>
                </Stat> */}
              {/* </StatGroup> */}
            </Stack>
          </PageHeader>

          <SimpleGrid columns={[1, 5]} gap={[2, 4]}>
            <PageCard>
              <Stat>
                <StatLabel>Total</StatLabel>
                <StatNumber>
                  <CurrencyLabel value={po?.total} />
                </StatNumber>
              </Stat>
            </PageCard>
            {/* <PageCard>
              <Stat>
                <StatLabel>Approval Required</StatLabel>
                <StatNumber>
                  <CurrencyLabel value={driftTotal} />
                </StatNumber>
              </Stat>
            </PageCard> */}

            <PageCard>
              <Stat>
                <StatLabel>Pending</StatLabel>
                <StatNumber>
                  <CurrencyLabel value={po?.pending} />
                </StatNumber>
              </Stat>
            </PageCard>
            <PageCard>
              <Stat>
                <StatLabel>Ordered</StatLabel>
                <StatNumber>
                  <CurrencyLabel value={po?.orderTotal} />
                </StatNumber>
              </Stat>
            </PageCard>
            <PageCard>
              <Stat>
                <StatLabel>Received</StatLabel>
                <StatNumber>
                  <CurrencyLabel value={po?.receiveTotal} />
                </StatNumber>
              </Stat>
            </PageCard>
            <PageCard>
              <Stat>
                <StatLabel>Back Ordered</StatLabel>
                <StatNumber>
                  <CurrencyLabel value={po?.backorderTotal} />
                </StatNumber>
              </Stat>
            </PageCard>
          </SimpleGrid>

          <Box as="section">
            <Flex gap={4}>
              <PageBody flexGrow={1}>
                <Stack dir="column"></Stack>
                <Tabs>
                  <TabList>
                    <Tab>Accounts</Tab>
                    {pendingRevision && <Tab>Approvers</Tab>}
                    <Tab>Line Items</Tab>
                    <Tab>Revisions</Tab>
                    <Tab>Dates</Tab>
                  </TabList>
                  <TabPanels>
                    {/* Accounts  */}
                    <TabPanel>
                      <Stack>
                        <Flex py={4}>
                          {po && (
                            <Button
                              colorScheme="blue"
                              isDisabled={!isNewRevisionPossible}
                              onClick={() =>
                                setDrawerSettings({
                                  ...drawerSettings,
                                  isOpen: true,
                                  po,
                                  initialStage:
                                    PurchaseOrderFormWorkflowStage.revision,
                                })
                              }
                              leftIcon={
                                // @ts-ignore
                                <FiPlus />
                              }
                            >
                              Reconcile Accounts with Line Items
                            </Button>
                          )}

                          <Spacer />
                        </Flex>
                        {!isNewRevisionPossible && (
                          <Alert status="info">
                            <AlertIcon />
                            <AlertTitle>To Reconcile Line Items</AlertTitle>
                            <AlertDescription>
                              First, make a change to this PO's line items and
                              then return here to reconcile the changes.
                            </AlertDescription>
                          </Alert>
                        )}
                        <TableContainer>
                          <Table variant="simple">
                            <Thead>
                              <Tr>
                                <Th>Account</Th>
                                {/* <Th>Drift</Th> */}
                                <Th>Pending</Th>
                                <Th>Total</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {!po?.accounts.length && (
                                <Tr>
                                  <Td colSpan={3}>
                                    <Center>No Accounts to Display</Center>
                                  </Td>
                                </Tr>
                              )}
                              {po?.accounts.map((a, i) => (
                                <Tr key={i}>
                                  <Td>
                                    {
                                      allSubAccounts?.find(
                                        (x) => x.subAccountId === a.subAccountId
                                      )?.fullSubAccountName
                                    }
                                  </Td>
                                  <Td>
                                    <CurrencyLabel value={a.pending} />
                                  </Td>
                                  <Td>
                                    <CurrencyLabel value={a.total} />
                                  </Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        </TableContainer>
                      </Stack>
                    </TabPanel>
                    {/* Approvers  */}
                    {pendingRevision && (
                      <TabPanel>
                        <ApproversSection
                          entityName={po?.name ?? ''}
                          revision={pendingRevision}
                        />
                      </TabPanel>
                    )}
                    {/* Line Items  */}
                    <TabPanel>
                      <Stack direction="column">
                        <Flex>
                          <EditPoLineItemsModal
                            purchaseOrderId={id}
                            purchaseOrderName={po?.name}
                            lineItems={po?.lineItems ?? []}
                          />
                        </Flex>
                        <TableContainer>
                          <Table variant="simple">
                            <Thead>
                              <Tr>
                                <Th>No.</Th>
                                <Th>Account</Th>
                                <Th>Description</Th>
                                <Th>Order QTY</Th>
                                <Th>Unit Price</Th>
                                <Th>Shipped</Th>
                                <Th>Received QTY</Th>
                                <Th>Received</Th>
                                <Th>Backorder QTY</Th>
                                <Th>Notes</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {!po?.lineItems.length && (
                                <Tr>
                                  <Td colSpan={10}>
                                    <Center>No Line Items to Display</Center>
                                  </Td>
                                </Tr>
                              )}
                              {po?.lineItems?.map((li, i) => (
                                <Tr key={i}>
                                  <Td>{i + 1}</Td>
                                  <Td>
                                    {
                                      allSubAccounts?.find(
                                        (x) =>
                                          x.subAccountId === li.subAccountId
                                      )?.fullSubAccountName
                                    }
                                  </Td>
                                  <Td>{li.description}</Td>
                                  <Td>{li.quantityOrdered}</Td>
                                  <Td>
                                    <CurrencyLabel value={li.unitPrice} />
                                  </Td>
                                  <Td>
                                    <DateDisplay
                                      value={li.revisedShipDate || li.shipDate}
                                    />
                                  </Td>
                                  <Td>{li.quantityReceived}</Td>
                                  <Td>
                                    <DateDisplay value={li.receivedDate} />
                                  </Td>
                                  <Td>{li.quantityBackordered}</Td>
                                  <Td>{li.notes}</Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        </TableContainer>
                      </Stack>
                    </TabPanel>
                    {/* Transactions  */}
                    <TabPanel>
                      <TableContainer>
                        <Table variant="simple">
                          <Thead>
                            <Tr>
                              <Th>Name</Th>
                              <Th>Total</Th>
                              <Th>Requested</Th>
                              <Th>Redeemed</Th>
                              <Th>Cancelled</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {!transactions?.itemCount && (
                              <Tr>
                                <Td colSpan={5}>
                                  <Center>No Revisions to Display</Center>
                                </Td>
                              </Tr>
                            )}
                            {transactions?.items.map((li, i) => (
                              <Tr key={i}>
                                <Td>{li.name}</Td>
                                <Td>
                                  <CurrencyLabel value={li.total} />
                                </Td>
                                <Td>
                                  <DateDisplay value={li.requested} />
                                </Td>
                                <Td>
                                  <DateDisplay value={li.redeemed} />
                                </Td>
                                <Td>
                                  <DateDisplay value={li.cancelled} />
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    </TabPanel>
                    {/* Dates  */}
                    <TabPanel>
                      <DateStack
                        dates={[
                          { label: 'Committed', value: po?.commitmentDate },
                          // { label: 'Created', value: po?.createdAt },
                          // { label: 'Last Updated', value: po?.updatedAt },
                        ]}
                      />
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </PageBody>
            </Flex>
          </Box>
        </Stack>
      </LoadingSkeleton>
    </Box>
  );
};
